import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ContentHeader, Field, focusFirstInvalid, Modal, QueryStatus, useToast } from "@cx/ui";
import { customFieldInput, type CustomFieldInput, type CustomFieldRow } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { useSession } from "../../session";
import { errorMessage } from "../../errors";

const TYPES = {
  text: "Texto",
  number: "Número inteiro",
  decimal: "Decimal",
  date: "Data",
  boolean: "Sim/Não",
  select: "Lista",
  multiselect: "Lista múltipla",
  url: "Link",
} as const;
const MODULES = { contacts: "Contatos", products: "Produtos" } as const;
type Module = keyof typeof MODULES;
type Draft = Partial<CustomFieldRow> & { optionsText?: string };
const MESSAGES: Record<string, string> = {
  label: "Informe o nome do campo (até 60 caracteres).",
  options: "Cada opção pode ter até 60 caracteres, com no máximo 50 opções.",
};

export function CustomFieldsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const [mod, setMod] = useState<Module>("contacts");
  const list = useQuery({
    queryKey: ["custom-fields", companyId, mod],
    queryFn: () => api<{ data: CustomFieldRow[] }>(`/custom-fields?module=${mod}`),
  });
  const [edit, setEdit] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const open = (d: Draft) => {
    setErrors({});
    setEdit(d);
  };
  const save = useMutation({
    mutationFn: ({ id, body }: { id?: string; body: CustomFieldInput }) =>
      api(id ? `/custom-fields/${id}` : "/custom-fields", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setEdit(null);
      toast("success", "Campo salvo");
      qc.invalidateQueries({ queryKey: ["custom-fields", companyId] });
    },
    onError: (e: Error) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  // safeParse: erro de validação vira mensagem no campo, não JSON de ZodError no toast (FE25).
  const submit = () => {
    if (!edit) return;
    const isList = edit.fieldType === "select" || edit.fieldType === "multiselect";
    const r = customFieldInput.safeParse({
      module: mod,
      label: edit.label ?? "",
      fieldType: edit.fieldType,
      required: !!edit.required,
      position: edit.position ?? 0,
      active: edit.active ?? true,
      options: isList
        ? (edit.optionsText ?? "")
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const i of r.error.issues) {
        const k = String(i.path[0] ?? "");
        e[k] ??= MESSAGES[k] ?? "Valor inválido.";
      }
      setErrors(e);
      focusFirstInvalid();
      return;
    }
    save.mutate({ id: edit.id, body: r.data });
  };
  return (
    <>
      <ContentHeader
        title={`Campos customizados — ${MODULES[mod]}`}
        breadcrumb={["Preferências", "Campos customizados"]}
        actions={
          can(perms, "custom_fields", "create") ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => open({ fieldType: "text", active: true })}
            >
              Incluir campo
            </button>
          ) : undefined
        }
      />
      <div className="row mb-3">
        <Field label="Módulo" className="col-sm-6 col-md-3">
          <select className="form-select" value={mod} onChange={(e) => setMod(e.target.value as Module)}>
            {Object.entries(MODULES).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && (
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th scope="col">Campo</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Obrigatório</th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      Nenhum campo customizado.
                    </td>
                  </tr>
                )}
                {list.data.data.map((c) => (
                  <tr key={c.id}>
                    <td>{c.label}</td>
                    <td>{TYPES[c.fieldType]}</td>
                    <td>{c.required ? "Sim" : "Não"}</td>
                    <td>{c.active ? "Ativo" : "Inativo"}</td>
                    <td className="text-end">
                      {can(perms, "custom_fields", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          aria-label={`Editar ${c.label}`}
                          onClick={() => open({ ...c, optionsText: c.options?.join("\n") })}
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal
        open={!!edit}
        title={edit?.id ? "Editar campo" : "Novo campo"}
        onClose={() => setEdit(null)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setEdit(null)}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" disabled={save.isPending} onClick={submit}>
              {save.isPending ? "Salvando…" : "Salvar"}
            </button>
          </>
        }
      >
        {edit && (
          <div className="row">
            <Field label="Nome do campo" required error={errors.label} className="col-12">
              <input
                value={edit.label ?? ""}
                maxLength={60}
                onChange={(e) => setEdit({ ...edit, label: e.target.value })}
              />
            </Field>
            <Field label="Tipo" className="col-6">
              <select
                className="form-select"
                value={edit.fieldType}
                onChange={(e) =>
                  setEdit({ ...edit, fieldType: e.target.value as CustomFieldRow["fieldType"] })
                }
              >
                {Object.entries(TYPES).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Obrigatório" className="col-3">
              <select
                className="form-select"
                value={String(!!edit.required)}
                onChange={(e) => setEdit({ ...edit, required: e.target.value === "true" })}
              >
                <option value="false">Não</option>
                <option value="true">Sim</option>
              </select>
            </Field>
            <Field label="Situação" className="col-3">
              <select
                className="form-select"
                value={String(edit.active ?? true)}
                onChange={(e) => setEdit({ ...edit, active: e.target.value === "true" })}
              >
                <option value="true">Ativo</option>
                <option value="false">Inativo</option>
              </select>
            </Field>
            {(edit.fieldType === "select" || edit.fieldType === "multiselect") && (
              <Field label="Opções (uma por linha)" error={errors.options} className="col-12">
                <textarea
                  rows={4}
                  value={edit.optionsText ?? ""}
                  onChange={(e) => setEdit({ ...edit, optionsText: e.target.value })}
                />
              </Field>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
